/**
 * ControlsFilterDrawer Component
 * 
 * Filter drawer for Control Activities (EX5)
 * Uses RightContentDrawer as base component
 * Uses search mode pattern for cycles and processes (like FinancialStatementItemDrawer)
 */

import React, { useState, useEffect } from 'react';
import { Button, Input, DatePicker, Select } from 'antd';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import styled from 'styled-components';
import { SearchOutlined, RightOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import RightContentDrawer from '../../../components/common/RightContentDrawer';
import ButtonGroup from '../../../components/common/ButtonGroup';
import CloseCircleIcon from '../../../assets/icons/ic-close-circle.svg?react';
import {
  ControlStatus,
  ControlCategory,
  ControlType,
  FrequencyType,
  EffectivenessStatus,
} from '../../../services/controlActivitiesService';
import { RiskRanking } from '../../../services/types';
import { searchCycles, searchProcesses, searchProcessOwners, CycleItem, ProcessItem, ProcessOwner } from '../../../services/hierarchyService';

// ========== TYPE DEFINITIONS ==========

export type CompletedStatus = 'NotStarted' | 'WIP' | 'Completed';

export interface ControlsFilterState {
  status?: ControlStatus[];
  cycles?: CycleItem[];
  processes?: ProcessItem[];
  controlCategory?: ControlCategory[];
  controlType?: ControlType[];
  frequency?: FrequencyType[];
  keyOrNonKey?: 'Key' | 'Non-Key';
  riskRanking?: RiskRanking[];
  processOwners?: ProcessOwner[];
  testingScope?: boolean; // true = In Scope, false = Out of Scope
  // Testing fields (Yes/No/NotStarted enum - arrays for multi-select)
  designEffective?: ('Yes' | 'No' | 'NotStarted')[];
  interimCompleted?: CompletedStatus[];
  finalCompleted?: CompletedStatus[];
  operatingEffectiveness?: ('Yes' | 'No' | 'NotStarted')[];
  completionDate?: string | null;
  // Navigation filter (from Process Hierarchy)
  effectivenessStatus?: EffectivenessStatus[];
  // Auditor filter (from Dashboard navigation)
  hasAuditorAssigned?: boolean; // true = has auditor, false = no auditor assigned
}

export interface ControlsFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: ControlsFilterState) => void;
  currentFilters: ControlsFilterState;
  annualReportId: string | null;
}

// ========== STYLED COMPONENTS ==========

const FilterSection = styled.div`
  margin-bottom: 24px;
`;

const FilterLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  color: #1F2937;
  margin-bottom: 8px;
`;


const StyledSelect = styled(Select)`
  width: 100%;
  
  .ant-select-selector {
    border-radius: 8px !important;
    min-height: 40px !important;
  }
`;

// Custom Select Tag for styled display
const SelectTag = styled.span<{ $backgroundColor: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: ${props => props.$backgroundColor};
  border: none;
  border-radius: 50px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 1;
  color: ${props => props.$color};
  margin: 2px;
`;

const SelectTagCloseIcon = styled(CloseCircleIcon)<{ $circleFill: string; $strokeColor: string }>`
  cursor: pointer;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  
  path:first-child {
    fill: ${props => props.$circleFill};
  }
  
  path:last-child {
    stroke: ${props => props.$strokeColor};
  }
`;

// Search mode styled components
const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SearchInputWrapper = styled.div`
  margin-bottom: 24px;

  .ant-input-affix-wrapper {
    height: 44px;
    border-radius: 6px;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;

    .ant-input {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #6B7280;
    }

    .anticon-search {
      color: #6B7280;
    }
  }
`;

const SearchResultsList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const SearchResultItem = styled.div<{ $selected: boolean }>`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#EFF6FF' : 'transparent'};
  border: 1px solid ${props => props.$selected ? '#2563EB' : '#E5E7EB'};

  &:hover {
    background: ${props => props.$selected ? '#EFF6FF' : '#F3F4F6'};
  }

  .item-label {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: ${props => props.$selected ? '#2563EB' : '#1F2937'};
    word-wrap: break-word;
    overflow-wrap: break-word;
    white-space: normal;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #9CA3AF;
  font-family: Inter;
  font-size: 14px;
`;

const TagContainer = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 32px 8px 12px;
  min-height: 44px;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  background: white;
  cursor: pointer;

  &:hover {
    border-color: #2563EB;
  }
`;

const TagContainerDisabled = styled(TagContainer)`
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
  
  &:hover {
    border-color: #D1D5DB;
  }
`;

const ArrowButton = styled.div`
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  
  .anticon {
    color: #6B7280;
    font-size: 12px;
    transition: color 0.2s;
  }

  &:hover .anticon {
    color: #2563EB;
  }
`;

const Tag = styled.span<{ 
  $backgroundColor: string; 
  $color: string;
  $isMoreTag?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  background: ${props => props.$backgroundColor};
  color: ${props => props.$color};
  cursor: ${props => props.$isMoreTag ? 'pointer' : 'default'};
  white-space: nowrap;
`;

const StyledCloseIcon = styled(CloseCircleIcon)<{ $circleFill: string; $strokeColor: string }>`
  cursor: pointer;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  
  path:first-child {
    fill: ${props => props.$circleFill};
  }
  
  path:last-child {
    stroke: ${props => props.$strokeColor};
  }
`;

const PlaceholderText = styled.span`
  color: #9CA3AF;
  font-family: Inter;
  font-size: 14px;
`;

const DisabledHint = styled.div`
  font-size: 12px;
  color: #9CA3AF;
  margin-top: 4px;
`;

// Date picker styled component
const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  height: 40px;
  border-radius: 8px !important;
`;

// ========== CONSTANTS ==========

const STATUS_OPTIONS: { value: ControlStatus; label: string }[] = [
  { value: 'Draft', label: 'Draft' },
  { value: 'Active', label: 'Active' },
];

const CATEGORY_OPTIONS: { value: ControlCategory; label: string }[] = [
  { value: 'Financial', label: 'Financial' },
  { value: 'Operating', label: 'Operational' },
  { value: 'Regulatory', label: 'Regulatory' },
];

const TYPE_OPTIONS: { value: ControlType; label: string }[] = [
  { value: 'Preventative', label: 'Preventative' },
  { value: 'Detective', label: 'Detective' },
  { value: 'Other', label: 'Other' },
];

const FREQUENCY_OPTIONS: { value: FrequencyType; label: string }[] = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Annually', label: 'Annually' },
];

const RISK_RANKING_OPTIONS: { value: RiskRanking; label: string }[] = [
  { value: 'High', label: 'High' },
  { value: 'Med', label: 'Medium' },
  { value: 'Low', label: 'Low' },
  { value: 'Unranked', label: 'Unranked' },
];

const EFFECTIVENESS_OPTIONS: { value: EffectivenessStatus; label: string }[] = [
  { value: 'effective', label: 'Effective' },
  { value: 'ineffective', label: 'Ineffective' },
  { value: 'untested', label: 'Untested' },
  { value: 'outofscope', label: 'Out Of Scope' },
];

const INTERIM_COMPLETED_OPTIONS: { value: CompletedStatus; label: string }[] = [
  { value: 'NotStarted', label: 'Not Started' },
  { value: 'WIP', label: 'WIP' },
  { value: 'Completed', label: 'Completed' },
];

const FINAL_COMPLETED_OPTIONS: { value: CompletedStatus; label: string }[] = [
  { value: 'NotStarted', label: 'Not Started' },
  { value: 'WIP', label: 'WIP' },
  { value: 'Completed', label: 'Completed' },
];

// Design Effective & Operating Effectiveness options (3 values)
const DESIGN_EFFECTIVENESS_OPTIONS: { value: 'Yes' | 'No' | 'NotStarted'; label: string }[] = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
  { value: 'NotStarted', label: 'Not Started' },
];

// Testing Scope options (2 values)
const TESTING_SCOPE_OPTIONS: { value: boolean; label: string }[] = [
  { value: true, label: 'In Scope' },
  { value: false, label: 'Out of Scope' },
];

// Key or Non-Key options (2 values)
const KEY_CONTROL_OPTIONS: { value: 'Key' | 'Non-Key'; label: string }[] = [
  { value: 'Key', label: 'Key' },
  { value: 'Non-Key', label: 'Non-Key' },
];

// Auditor Assigned options (2 values)
const AUDITOR_ASSIGNED_OPTIONS: { value: boolean; label: string }[] = [
  { value: true, label: 'Assigned' },
  { value: false, label: 'Unassigned' },
];

// ========== HELPER FUNCTIONS ==========

const getActiveFilterCount = (filters: ControlsFilterState): number => {
  let count = 0;
  if (filters.status?.length) count++;
  if (filters.cycles?.length) count++;
  if (filters.processes?.length) count++;
  if (filters.controlCategory?.length) count++;
  if (filters.controlType?.length) count++;
  if (filters.frequency?.length) count++;
  if (filters.keyOrNonKey) count++;
  if (filters.riskRanking?.length) count++;
  if (filters.processOwners?.length) count++;
  if (filters.testingScope !== null && filters.testingScope !== undefined) count++;
  if (filters.designEffective?.length) count++;
  if (filters.interimCompleted?.length) count++;
  if (filters.finalCompleted?.length) count++;
  if (filters.operatingEffectiveness?.length) count++;
  if (filters.completionDate) count++;
  if (filters.effectivenessStatus !== null && filters.effectivenessStatus !== undefined) count++;
  if (filters.hasAuditorAssigned !== null && filters.hasAuditorAssigned !== undefined) count++;
  return count;
};

const getEmptyFilters = (): ControlsFilterState => ({
  status: undefined,
  cycles: undefined,
  processes: undefined,
  controlCategory: undefined,
  controlType: undefined,
  frequency: undefined,
  keyOrNonKey: undefined,
  riskRanking: undefined,
  processOwners: undefined,
  testingScope: undefined,
  designEffective: undefined,
  interimCompleted: undefined,
  finalCompleted: undefined,
  operatingEffectiveness: undefined,
  completionDate: undefined,
  effectivenessStatus: undefined,
  hasAuditorAssigned: undefined,
});

// ========== MAIN COMPONENT ==========

const ControlsFilterDrawer: React.FC<ControlsFilterDrawerProps> = ({
  open,
  onClose,
  onApply,
  currentFilters,
  annualReportId,
}) => {
  const [localFilters, setLocalFilters] = useState<ControlsFilterState>(currentFilters);

  const companyId = localStorage.getItem("companyId");
  
  // Search mode states - only for cycle, process, processOwner
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchType, setSearchType] = useState<'cycle' | 'process' | 'processOwner'>('cycle');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // Search results
  const [cycleResults, setCycleResults] = useState<CycleItem[]>([]);
  const [processResults, setProcessResults] = useState<ProcessItem[]>([]);
  const [processOwnerResults, setProcessOwnerResults] = useState<ProcessOwner[]>([]);
  
  // Temp selections (for search mode - only cycle, process, processOwner)
  const [tempSelectedCycles, setTempSelectedCycles] = useState<CycleItem[]>([]);
  const [tempSelectedProcesses, setTempSelectedProcesses] = useState<ProcessItem[]>([]);
  const [tempSelectedProcessOwners, setTempSelectedProcessOwners] = useState<ProcessOwner[]>([]);

  // Helper function to get status colors
  const getStatusColors = (status: ControlStatus) => {
    switch (status) {
      case 'Active': return { bg: '#CCFBF1', text: '#0D9488', closeBg: '#99F6E4' };
      case 'Draft': return { bg: '#FEF3C7', text: '#B45309', closeBg: '#FDE68A' };
      default: return { bg: '#F3F4F6', text: '#374151', closeBg: '#E5E7EB' };
    }
  };

  // Helper function to get risk ranking colors
  const getRiskColors = (risk: RiskRanking) => {
    switch (risk) {
      case 'High': return { bg: '#FEE2E2', text: '#EF4444', closeBg: '#FECACA' };
      case 'Med': return { bg: '#FEF3C7', text: '#B45309', closeBg: '#FDE68A' };
      case 'Low': return { bg: '#D1FAE5', text: '#059669', closeBg: '#A7F3D0' };
      default: return { bg: '#DBEAFE', text: '#2563EB', closeBg: '#BFDBFE' };
    }
  };

  // Custom tag render for Status Select
  const renderStatusTag = (props: CustomTagProps) => {
    const { label, value, closable, onClose } = props;
    const colors = getStatusColors(value as ControlStatus);
    const onPreventMouseDown = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
    return (
      <SelectTag $backgroundColor={colors.bg} $color={colors.text} onMouseDown={onPreventMouseDown}>
        {label}
        {closable && <SelectTagCloseIcon $circleFill={colors.closeBg} $strokeColor={colors.text} onClick={(e) => { e.stopPropagation(); onClose(); }} />}
      </SelectTag>
    );
  };

  // Custom tag render for Risk Ranking Select
  const renderRiskTag = (props: CustomTagProps) => {
    const { label, value, closable, onClose } = props;
    const colors = getRiskColors(value as RiskRanking);
    const onPreventMouseDown = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
    return (
      <SelectTag $backgroundColor={colors.bg} $color={colors.text} onMouseDown={onPreventMouseDown}>
        {label}
        {closable && <SelectTagCloseIcon $circleFill={colors.closeBg} $strokeColor={colors.text} onClick={(e) => { e.stopPropagation(); onClose(); }} />}
      </SelectTag>
    );
  };

  // Custom tag render for generic blue tags (Category, Type, Frequency, etc.)
  const renderBlueTag = (props: CustomTagProps) => {
    const { label, closable, onClose } = props;
    const onPreventMouseDown = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
    return (
      <SelectTag $backgroundColor="#DBEAFE" $color="#1D4ED8" onMouseDown={onPreventMouseDown}>
        {label}
        {closable && <SelectTagCloseIcon $circleFill="#BFDBFE" $strokeColor="#1D4ED8" onClick={(e) => { e.stopPropagation(); onClose(); }} />}
      </SelectTag>
    );
  };

  // Custom tag render for Key Control (Key = green, Non-Key = gray)
  const renderKeyControlTag = (props: CustomTagProps) => {
    const { label, value, closable, onClose } = props;
    const isKey = value === 'Key';
    const colors = isKey 
      ? { bg: '#D1FAE5', text: '#059669', closeBg: '#A7F3D0' }
      : { bg: '#F3F4F6', text: '#6B7280', closeBg: '#E5E7EB' };
    const onPreventMouseDown = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
    return (
      <SelectTag $backgroundColor={colors.bg} $color={colors.text} onMouseDown={onPreventMouseDown}>
        {label}
        {closable && <SelectTagCloseIcon $circleFill={colors.closeBg} $strokeColor={colors.text} onClick={(e) => { e.stopPropagation(); onClose(); }} />}
      </SelectTag>
    );
  };

  // Custom tag render for Testing Scope (In Scope = green, Out of Scope = red)
  const renderTestingScopeTag = (props: CustomTagProps) => {
    const { label, value, closable, onClose } = props;
    const inScope = value === true;
    const colors = inScope 
      ? { bg: '#D1FAE5', text: '#059669', closeBg: '#A7F3D0' }
      : { bg: '#FEE2E2', text: '#DC2626', closeBg: '#FECACA' };
    const onPreventMouseDown = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
    return (
      <SelectTag $backgroundColor={colors.bg} $color={colors.text} onMouseDown={onPreventMouseDown}>
        {label}
        {closable && <SelectTagCloseIcon $circleFill={colors.closeBg} $strokeColor={colors.text} onClick={(e) => { e.stopPropagation(); onClose(); }} />}
      </SelectTag>
    );
  };

  // Custom tag render for Auditor Assigned (Assigned = green, Unassigned = red)
  const renderAuditorAssignedTag = (props: CustomTagProps) => {
    const { label, value, closable, onClose } = props;
    const isAssigned = value === true;
    const colors = isAssigned 
      ? { bg: '#D1FAE5', text: '#059669', closeBg: '#A7F3D0' }
      : { bg: '#FEE2E2', text: '#DC2626', closeBg: '#FECACA' };
    const onPreventMouseDown = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
    return (
      <SelectTag $backgroundColor={colors.bg} $color={colors.text} onMouseDown={onPreventMouseDown}>
        {label}
        {closable && <SelectTagCloseIcon $circleFill={colors.closeBg} $strokeColor={colors.text} onClick={(e) => { e.stopPropagation(); onClose(); }} />}
      </SelectTag>
    );
  };

  // Custom tag render for Effectiveness (Yes = green, No = red, Not Started = gray)
  const renderEffectivenessTag = (props: CustomTagProps) => {
    const { label, value, closable, onClose } = props;
    let colors = { bg: '#F3F4F6', text: '#6B7280', closeBg: '#E5E7EB' }; // Not Started
    if (value === 'Yes' || value === 'effective') colors = { bg: '#D1FAE5', text: '#059669', closeBg: '#A7F3D0' };
    if (value === 'No' || value === 'ineffective') colors = { bg: '#FEE2E2', text: '#DC2626', closeBg: '#FECACA' };
    const onPreventMouseDown = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
    return (
      <SelectTag $backgroundColor={colors.bg} $color={colors.text} onMouseDown={onPreventMouseDown}>
        {label}
        {closable && <SelectTagCloseIcon $circleFill={colors.closeBg} $strokeColor={colors.text} onClick={(e) => { e.stopPropagation(); onClose(); }} />}
      </SelectTag>
    );
  };

  // Sync local filters when drawer opens or current filters change
  useEffect(() => {
    if (open) {
      setLocalFilters(currentFilters);
      setIsSearchMode(false);
    }
  }, [open, currentFilters]);

  // Search mode handlers - only for cycle, process, processOwner
  const enterSearchMode = (type: 'cycle' | 'process' | 'processOwner') => {
    setSearchType(type);
    setIsSearchMode(true);
    setSearchQuery('');
    
    // Load existing selections into temp states
    setTempSelectedCycles([...(localFilters.cycles || [])]);
    setTempSelectedProcesses([...(localFilters.processes || [])]);
    setTempSelectedProcessOwners([...(localFilters.processOwners || [])]);
    
    // Clear previous results
    setCycleResults([]);
    setProcessResults([]);
    setProcessOwnerResults([]);
  };

  const exitSearchMode = () => {
    setIsSearchMode(false);
    setSearchQuery('');
    setTempSelectedCycles([]);
    setTempSelectedProcesses([]);
    setTempSelectedProcessOwners([]);
    setCycleResults([]);
    setProcessResults([]);
    setProcessOwnerResults([]);
  };

  // Fetch search results when query changes
  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!isSearchMode) return;
      
      // processOwner search needs companyId, cycle/process search needs annualReportId
      if (searchType === 'processOwner' && !companyId) return;
      if (searchType !== 'processOwner' && !annualReportId) return;
      
      setIsSearching(true);
      
      try {
        if (searchType === 'cycle') {
          const results = await searchCycles({
            annualReportId: annualReportId!,
            searchTerm: searchQuery,
          });
          setCycleResults(results);
        } else if (searchType === 'process') {
          // Extract cycleIds from selected cycles
          const cycleIds = tempSelectedCycles.map(cycle => cycle.cycleId);
          const results = await searchProcesses({
            annualReportId: annualReportId!,
            searchTerm: searchQuery,
            cycleIds,
          });
          setProcessResults(results);
        } else if (searchType === 'processOwner') {
          const results = await searchProcessOwners({
            companyId: companyId || '',
            searchTerm: searchQuery,
          });
          setProcessOwnerResults(results);
        }
      } catch (error) {
        console.error('Search failed:', error);
        if (searchType === 'cycle') {
          setCycleResults([]);
        } else if (searchType === 'process') {
          setProcessResults([]);
        } else {
          setProcessOwnerResults([]);
        }
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce search
    const timeoutId = setTimeout(fetchSearchResults, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchType, isSearchMode, annualReportId, tempSelectedCycles, companyId]);

  const handleSearchResultClick = (value: CycleItem | ProcessItem | ProcessOwner | ControlStatus | RiskRanking) => {
    if (searchType === 'cycle') {
      const cycleValue = value as CycleItem;
      const exists = tempSelectedCycles.some(c => c.cycleId === cycleValue.cycleId);
      
      if (exists) {
        setTempSelectedCycles(tempSelectedCycles.filter(v => v.cycleId !== cycleValue.cycleId));
      } else {
        setTempSelectedCycles([...tempSelectedCycles, cycleValue]);
      }
    } else if (searchType === 'process') {
      const processValue = value as ProcessItem;
      const exists = tempSelectedProcesses.some(p => p.processId === processValue.processId);
      if (exists) {
        setTempSelectedProcesses(tempSelectedProcesses.filter(v => v.processId !== processValue.processId));
      } else {
        setTempSelectedProcesses([...tempSelectedProcesses, processValue]);
      }
    } else if (searchType === 'processOwner') {
      const ownerValue = value as ProcessOwner;
      const exists = tempSelectedProcessOwners.some(o => o.userId === ownerValue.userId);
      if (exists) {
        setTempSelectedProcessOwners(tempSelectedProcessOwners.filter(v => v.userId !== ownerValue.userId));
      } else {
        setTempSelectedProcessOwners([...tempSelectedProcessOwners, ownerValue]);
      }
    }
  };

  const applySearchSelection = () => {
    if (searchType === 'cycle') {
      setLocalFilters(prev => ({
        ...prev,
        cycles: tempSelectedCycles,
        // Clear processes when cycles change
        processes: [],
      }));
    } else if (searchType === 'process') {
      setLocalFilters(prev => ({
        ...prev,
        processes: tempSelectedProcesses,
      }));
    } else if (searchType === 'processOwner') {
      setLocalFilters(prev => ({
        ...prev,
        processOwners: tempSelectedProcessOwners,
      }));
    }
    
    exitSearchMode();
  };

  const handleRemoveCycle = (cycleId: string) => {
    setLocalFilters(prev => ({
      ...prev,
      cycles: (prev.cycles || []).filter(c => c.cycleId !== cycleId),
      // Clear processes when cycles change
      processes: [],
    }));
  };

  const handleRemoveProcess = (processId: string) => {
    setLocalFilters(prev => ({
      ...prev,
      processes: (prev.processes || []).filter(p => p.processId !== processId),
    }));
  };

  const handleClearAll = () => {
    const emptyFilters = getEmptyFilters();
    setLocalFilters(emptyFilters);
    onApply(emptyFilters);
    onClose();
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleDrawerClose = () => {
    if (isSearchMode) {
      exitSearchMode();
    } else {
      onClose();
    }
  };

  const activeCount = getActiveFilterCount(localFilters);
  const selectedCycles = localFilters.cycles || [];
  const selectedProcesses = localFilters.processes || [];
  const canSelectProcess = selectedCycles.length > 0;

  // Render search UI - only for cycle, process, processOwner
  const renderSearchUI = () => {
    const getSearchConfig = () => {
      switch (searchType) {
        case 'cycle':
          return {
            results: cycleResults,
            placeholder: 'Search Cycle...',
            emptyLabel: 'cycles',
            getItemId: (item: CycleItem) => item.cycleId,
            getItemLabel: (item: CycleItem) => `${item.cycleCode} - ${item.cycleName}`,
            isSelected: (item: CycleItem) => tempSelectedCycles.some(c => c.cycleId === item.cycleId),
          };
        case 'process':
          return {
            results: processResults,
            placeholder: 'Search Process...',
            emptyLabel: 'processes',
            getItemId: (item: ProcessItem) => item.processId,
            getItemLabel: (item: ProcessItem) => `${item.processCode} - ${item.processName}`,
            isSelected: (item: ProcessItem) => tempSelectedProcesses.some(p => p.processId === item.processId),
          };
        case 'processOwner':
          return {
            results: processOwnerResults,
            placeholder: 'Search Process Owner...',
            emptyLabel: 'process owners',
            getItemId: (item: ProcessOwner) => item.userId,
            getItemLabel: (item: ProcessOwner) => item.userName + (item.title ? ` (${item.title})` : ''),
            isSelected: (item: ProcessOwner) => tempSelectedProcessOwners.some(o => o.userId === item.userId),
          };
      }
    };

    const config = getSearchConfig();

    return (
      <SearchContainer>
        <SearchInputWrapper>
          <Input
            prefix={<SearchOutlined />}
            suffix={
              isSearching && searchQuery !== '' ? (
                <LoadingOutlined style={{ color: '#2563EB' }} />
              ) : null
            }
            placeholder={config.placeholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </SearchInputWrapper>

        <SearchResultsList>
          {config.results.length === 0 ? (
            searchQuery === '' ? (
              <EmptyState>{isSearching ? 'Loading...' : `There are no ${config.emptyLabel} to display`}</EmptyState>
            ) : (
              <EmptyState>{isSearching ? 'Searching...' : 'There are no results found'}</EmptyState>
            )
          ): (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            config.results.map((item: any) => {
              const itemId = config.getItemId(item);
              const itemLabel = config.getItemLabel(item);
              const isSelected = config.isSelected(item);
              return (
                <SearchResultItem
                  key={itemId}
                  $selected={isSelected}
                  onClick={() => handleSearchResultClick(item)}
                >
                  <div className="item-label">{itemLabel}</div>
                </SearchResultItem>
              );
            })
          )}
        </SearchResultsList>
      </SearchContainer>
    );
  };

  // Render filter UI
  const renderFilterUI = () => (
    <>
      {/* Status Filter - Dropdown with styled tags */}
      <FilterSection>
        <FilterLabel>Status</FilterLabel>
        <StyledSelect
          mode="multiple"
          placeholder="Select status"
          value={localFilters.status || []}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, status: value as ControlStatus[] }))}
          options={STATUS_OPTIONS}
          tagRender={renderStatusTag}
          allowClear
        />
      </FilterSection>

      {/* Cycle Filter - Search Mode */}
      <FilterSection>
        <FilterLabel>Cycle</FilterLabel>
        <TagContainer onClick={() => enterSearchMode('cycle')}>
          {selectedCycles.length === 0 ? (
            <PlaceholderText>Select cycles...</PlaceholderText>
          ) : (
            selectedCycles.map(cycle => (
              <Tag 
                key={cycle.cycleId} 
                $backgroundColor="#E0E7FF" 
                $color="#4338CA"
              >
                {cycle.cycleCode} - {cycle.cycleName}
                <StyledCloseIcon
                  $circleFill="#C7D2FE"
                  $strokeColor="#4338CA"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveCycle(cycle.cycleId);
                  }}
                />
              </Tag>
            ))
          )}
          <ArrowButton>
            <RightOutlined />
          </ArrowButton>
        </TagContainer>
      </FilterSection>

      {/* Process Filter - Search Mode (only enabled when cycle is selected) */}
      <FilterSection>
        <FilterLabel>Process</FilterLabel>
        {canSelectProcess ? (
          <TagContainer onClick={() => enterSearchMode('process')}>
            {selectedProcesses.length === 0 ? (
              <PlaceholderText>Select processes...</PlaceholderText>
            ) : (
              selectedProcesses.map(process => (
                <Tag 
                  key={process.processId} 
                  $backgroundColor="#DCFCE7" 
                  $color="#166534"
                >
                  {process.processCode} - {process.processName}
                  <StyledCloseIcon
                    $circleFill="#BBF7D0"
                    $strokeColor="#166534"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveProcess(process.processId);
                    }}
                  />
                </Tag>
              ))
            )}
            <ArrowButton>
              <RightOutlined />
            </ArrowButton>
          </TagContainer>
        ) : selectedProcesses.length > 0 ? (
          <TagContainer style={{ cursor: 'default' }}>
            {selectedProcesses.map(process => (
              <Tag
                key={process.processId}
                $backgroundColor="#DCFCE7"
                $color="#166534"
              >
                {process.processCode} - {process.processName}
                <StyledCloseIcon
                  $circleFill="#BBF7D0"
                  $strokeColor="#166534"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveProcess(process.processId);
                  }}
                />
              </Tag>
            ))}
          </TagContainer>
        ) : (
          <>
            <TagContainerDisabled>
              <PlaceholderText>Select cycles first...</PlaceholderText>
              <ArrowButton>
                <RightOutlined />
              </ArrowButton>
            </TagContainerDisabled>
            <DisabledHint>Please select at least one cycle first</DisabledHint>
          </>
        )}
      </FilterSection>

      {/* Control Category Filter - Dropdown with styled tags */}
      <FilterSection>
        <FilterLabel>Control Category</FilterLabel>
        <StyledSelect
          mode="multiple"
          placeholder="Select category"
          value={localFilters.controlCategory || []}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, controlCategory: value as ControlCategory[] }))}
          options={CATEGORY_OPTIONS}
          tagRender={renderBlueTag}
          allowClear
        />
      </FilterSection>

      {/* Control Type Filter - Dropdown with styled tags */}
      <FilterSection>
        <FilterLabel>Control Type</FilterLabel>
        <StyledSelect
          mode="multiple"
          placeholder="Select type"
          value={localFilters.controlType || []}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, controlType: value as ControlType[] }))}
          options={TYPE_OPTIONS}
          tagRender={renderBlueTag}
          allowClear
        />
      </FilterSection>

      {/* Frequency Filter - Dropdown with styled tags */}
      <FilterSection>
        <FilterLabel>Frequency</FilterLabel>
        <StyledSelect
          mode="multiple"
          placeholder="Select frequency"
          value={localFilters.frequency || []}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, frequency: value as FrequencyType[] }))}
          options={FREQUENCY_OPTIONS}
          tagRender={renderBlueTag}
          allowClear
        />
      </FilterSection>

      {/* Key or Non-Key Filter - Single select with tagRender */}
      <FilterSection>
        <FilterLabel>Key or Non-Key</FilterLabel>
        <StyledSelect
          placeholder="Select"
          value={localFilters.keyOrNonKey || undefined}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, keyOrNonKey: value as 'Key' | 'Non-Key' | undefined }))}
          options={KEY_CONTROL_OPTIONS}
          tagRender={renderKeyControlTag}
          allowClear
        />
      </FilterSection>

      {/* Risk Ranking Filter - Dropdown with styled tags */}
      <FilterSection>
        <FilterLabel>Risk Ranking</FilterLabel>
        <StyledSelect
          mode="multiple"
          placeholder="Select risk ranking"
          value={localFilters.riskRanking?.filter(r => r != 'null') || []}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, riskRanking: value as RiskRanking[] }))}
          options={RISK_RANKING_OPTIONS}
          tagRender={renderRiskTag}
          allowClear
        />
      </FilterSection>

      {/* Process Owner Filter - Search Mode */}
      <FilterSection>
        <FilterLabel>Process Owner</FilterLabel>
        <TagContainer onClick={() => enterSearchMode('processOwner')}>
          {(localFilters.processOwners || []).length === 0 ? (
            <PlaceholderText>Search process owner</PlaceholderText>
          ) : (
            <>
              {(localFilters.processOwners || []).slice(0, 2).map((owner) => (
                <Tag 
                  key={owner.userId} 
                  $backgroundColor="#DBEAFE" 
                  $color="#1D4ED8"
                >
                  {owner.userName}
                  <StyledCloseIcon
                    $circleFill="#93C5FD"
                    $strokeColor="#1D4ED8"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLocalFilters(prev => ({
                        ...prev,
                        processOwners: prev.processOwners?.filter(o => o.userId !== owner.userId),
                      }));
                    }}
                  />
                </Tag>
              ))}
              {(localFilters.processOwners || []).length > 2 && (
                <Tag $backgroundColor="#F3F4F6" $color="#6B7280" $isMoreTag>
                  +{(localFilters.processOwners || []).length - 2}
                </Tag>
              )}
            </>
          )}
          <ArrowButton>
            <RightOutlined />
          </ArrowButton>
        </TagContainer>
      </FilterSection>

      {/* Testing Scope Filter - Single select with tagRender */}
      <FilterSection>
        <FilterLabel>Testing Scope</FilterLabel>
        <StyledSelect
          placeholder="Select"
          value={localFilters.testingScope !== null && localFilters.testingScope !== undefined ? localFilters.testingScope : undefined}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, testingScope: value as boolean | undefined }))}
          options={TESTING_SCOPE_OPTIONS}
          tagRender={renderTestingScopeTag}
          allowClear
        />
      </FilterSection>

      {/* Auditor Assigned Filter - Single select with tagRender */}
      <FilterSection>
        <FilterLabel>Auditor Assigned</FilterLabel>
        <StyledSelect
          placeholder="Select"
          value={localFilters.hasAuditorAssigned !== null && localFilters.hasAuditorAssigned !== undefined ? localFilters.hasAuditorAssigned : undefined}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, hasAuditorAssigned: value as boolean | undefined }))}
          options={AUDITOR_ASSIGNED_OPTIONS}
          tagRender={renderAuditorAssignedTag}
          allowClear
        />
      </FilterSection>

      {/* Design Effective Filter - Multiple select with tagRender */}
      <FilterSection>
        <FilterLabel>Design Effective</FilterLabel>
        <StyledSelect
          mode="multiple"
          placeholder="Select"
          value={localFilters.designEffective || []}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, designEffective: value as ('Yes' | 'No' | 'NotStarted')[] }))}
          options={DESIGN_EFFECTIVENESS_OPTIONS}
          tagRender={renderEffectivenessTag}
          allowClear
        />
      </FilterSection>

      {/* Interim Completed Filter - Dropdown with styled tags */}
      <FilterSection>
        <FilterLabel>Interim Completed</FilterLabel>
        <StyledSelect
          mode="multiple"
          placeholder="Select status"
          value={localFilters.interimCompleted || []}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, interimCompleted: value as CompletedStatus[] }))}
          options={INTERIM_COMPLETED_OPTIONS}
          tagRender={renderBlueTag}
          allowClear
        />
      </FilterSection>

      {/* Final Completed Filter - Dropdown with styled tags */}
      <FilterSection>
        <FilterLabel>Final Completed</FilterLabel>
        <StyledSelect
          mode="multiple"
          placeholder="Select status"
          value={localFilters.finalCompleted || []}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, finalCompleted: value as CompletedStatus[] }))}
          options={FINAL_COMPLETED_OPTIONS}
          tagRender={renderBlueTag}
          allowClear
        />
      </FilterSection>

      {/* Operating Effectiveness Filter - Multiple select with tagRender */}
      <FilterSection>
        <FilterLabel>Operating Effectiveness</FilterLabel>
        <StyledSelect
          mode="multiple"
          placeholder="Select"
          value={localFilters.operatingEffectiveness || []}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, operatingEffectiveness: value as ('Yes' | 'No' | 'NotStarted')[] }))}
          options={DESIGN_EFFECTIVENESS_OPTIONS}
          tagRender={renderEffectivenessTag}
          allowClear
        />
      </FilterSection>

      {/* Completion Date Filter - Single date with equal operator */}
      <FilterSection>
        <FilterLabel>Completion Date</FilterLabel>
        <StyledDatePicker
          placeholder="Select date"
          format="YYYY-MM-DD"
          value={localFilters.completionDate ? dayjs(localFilters.completionDate) : null}
          onChange={(date) => setLocalFilters(prev => ({
            ...prev,
            completionDate: date ? (date as Dayjs).format('YYYY-MM-DD') : null,
          }))}
          style={{ width: '100%' }}
        />
      </FilterSection>

      {/* Effectiveness Status Filter - Dropdown with styled tags */}
      <FilterSection>
        <FilterLabel>Effectiveness Status</FilterLabel>
        <StyledSelect
          mode="multiple"
          placeholder="Select effectiveness"
          value={localFilters.effectivenessStatus || []}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, effectivenessStatus: value as EffectivenessStatus[] }))}
          options={EFFECTIVENESS_OPTIONS}
          tagRender={renderEffectivenessTag}
          allowClear
        />
      </FilterSection>
    </>
  );

  return (
    <RightContentDrawer
      open={open}
      onClose={handleDrawerClose}
      title={isSearchMode ? `Select ${
        searchType === 'cycle' ? 'Cycles' : 
        searchType === 'process' ? 'Processes' : 
        'Process Owners'
      }` : 'Filter'}
      width={600}
      resizable={true}
      footer={
        isSearchMode ? (
          <ButtonGroup>
            <Button type="default" onClick={exitSearchMode}>
              Go back
            </Button>
            <Button type="primary" onClick={applySearchSelection}>
              Done
            </Button>
          </ButtonGroup>
        ) : (
          <ButtonGroup>
            {activeCount > 0 ? (
              <Button type="default" onClick={handleClearAll}>
                Clear Filter
              </Button>
            ) : (
              <Button type="default" onClick={onClose}>
                Cancel
              </Button>
            )}
            <Button type="primary" onClick={handleApply}>
              Apply Filters
            </Button>
          </ButtonGroup>
        )
      }
    >
      {isSearchMode ? renderSearchUI() : renderFilterUI()}
    </RightContentDrawer>
  );
};

export default ControlsFilterDrawer;
